// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { Table, Button, Icon, Message, Label, Header, Pagination, PaginationProps } from 'semantic-ui-react';
import { brokerageProviderApi, BrokerageProvider } from '../api/brokerageProviderApi';
import { providerApi, Provider } from '../api/providerApi';
import { providerEmailOverridesApi, ProviderEmailOverride } from '../api/providerEmailOverridesApi';
import PolicyTypeBrokerageModal from './PolicyTypeBrokerageModal';

interface ProviderConfigSectionProps {
  brokerageId: string;
  onProviderSelect?: (providerId: string) => void;
  selectedProviderId?: string;
}

function ProviderConfigSection({ brokerageId, onProviderSelect, selectedProviderId }: Readonly<ProviderConfigSectionProps>) {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [brokerageProviders, setBrokerageProviders] = useState<BrokerageProvider[]>([]);
  const [emailOverrides, setEmailOverrides] = useState<ProviderEmailOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProviderForModal, setSelectedProviderForModal] = useState<Provider | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load all providers with pagination
      const providersData = await providerApi.getAll({ page: currentPage, size: pageSize });
      setProviders(providersData.content);
      setTotalPages(providersData.totalPages);
      setTotalElements(providersData.totalElements);

      // Load ALL brokerage provider configurations (not paginated)
      // We need all of them so we can look up any provider's config on any page
      const brokerageProvidersData = await brokerageProviderApi.getAll(brokerageId, { size: 1000 });
      setBrokerageProviders(brokerageProvidersData.content);

      // Load provider-level email overrides for this brokerage
      const emailOverridesData = await providerEmailOverridesApi.getProviderLevelOverrides(brokerageId);
      setEmailOverrides(emailOverridesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [brokerageId, currentPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleProvider = async (providerId: string, currentlyEnabled: boolean) => {
    try {
      if (currentlyEnabled) {
        await brokerageProviderApi.disable(brokerageId, providerId);
      } else {
        await brokerageProviderApi.enable(brokerageId, providerId);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle provider');
    }
  };

  const handlePageChange = (_: React.MouseEvent<HTMLAnchorElement>, data: PaginationProps) => {
    const newPage = (data.activePage as number) - 1; // Semantic UI uses 1-based, backend uses 0-based
    setCurrentPage(newPage);
  };

  const getProviderConfig = (providerId: string): BrokerageProvider | undefined => {
    return brokerageProviders.find(bp => bp.providerId === providerId);
  };

  const getEmailOverride = (providerId: string): ProviderEmailOverride | undefined => {
    return emailOverrides.find(
      (override) => override.providerId === providerId && override.policyTypeId === null
    );
  };

  return (
    <div>
      <Header as='h3' dividing>
        Insurance Providers
        <Header.Subheader>Enable providers and configure settings (Provider Override)</Header.Subheader>
      </Header>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Table celled selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Provider</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell>LOA Template</Table.HeaderCell>
            <Table.HeaderCell>Email Templates</Table.HeaderCell>
            <Table.HeaderCell>Policy Types</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {providers.map(provider => {
            const config = getProviderConfig(provider.id!);
            const emailOverride = getEmailOverride(provider.id!);
            const isEnabled = config?.isEnabled || false;
            const isSelected = selectedProviderId === provider.id;
            const hasEmailTemplates = config?.emailSubjectTemplate || config?.emailBodyTemplateHtml;

            return (
              <Table.Row
                key={provider.id}
                active={isSelected}
                onClick={() => onProviderSelect?.(provider.id!)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Cell>
                  <Header as='h4'>
                    <Header.Content>
                      {provider.name}
                      {provider.logoUrl && (
                        <img
                          src={provider.logoUrl}
                          alt={provider.name}
                          style={{ height: '24px', marginLeft: '10px', verticalAlign: 'middle' }}
                        />
                      )}
                      <Header.Subheader>{provider.description}</Header.Subheader>
                    </Header.Content>
                  </Header>
                </Table.Cell>

                <Table.Cell>
                  <Label color={isEnabled ? 'green' : 'grey'}>
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </Label>
                </Table.Cell>

                <Table.Cell>
                  <div>
                    {emailOverride ? (
                      <span>
                        <Icon name='mail' color='blue' />
                        {emailOverride.overrideEmail}
                        <Label size='mini' color='blue' style={{ marginLeft: '5px' }}>
                          Override
                        </Label>
                      </span>
                    ) : (
                      <span>
                        <Icon name='mail outline' color='grey' />
                        {provider.email}
                      </span>
                    )}
                  </div>
                </Table.Cell>

                <Table.Cell>
                  {config ? (
                    <div>
                      {config.usingDefaultTemplate ? (
                        <span>
                          <Icon name='building' color='grey' />
                          Default
                        </span>
                      ) : (
                        <span>
                          <Icon name='file' color='blue' />
                          {config.customTemplateName || 'Custom'}
                          <Label size='mini' color='blue' style={{ marginLeft: '5px' }}>
                            Override
                          </Label>
                        </span>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>Default</span>
                  )}
                </Table.Cell>

                <Table.Cell>
                  {hasEmailTemplates ? (
                    <span>
                      <Icon name='envelope' color='blue' />
                      Custom
                      <Label size='mini' color='blue' style={{ marginLeft: '5px' }}>
                        Level 2
                      </Label>
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>
                      <Icon name='envelope outline' color='grey' />
                      Default
                    </span>
                  )}
                </Table.Cell>

                <Table.Cell>
                  {config && config.policyTypeCount != null ? (
                    <span>
                      <Icon name='file alternate outline' color={(config.enabledPolicyTypeCount ?? 0) > 0 ? 'blue' : 'grey'} />
                      {config.enabledPolicyTypeCount ?? 0}/{config.policyTypeCount} enabled
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>—</span>
                  )}
                </Table.Cell>

                <Table.Cell textAlign='center' style={{ whiteSpace: 'nowrap' }}>
                  <Button
                    icon={isEnabled ? 'ban' : 'checkmark'}
                    color={isEnabled ? 'red' : 'green'}
                    size='small'
                    title={isEnabled ? 'Disable provider' : 'Enable provider'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleProvider(provider.id!, isEnabled);
                    }}
                    disabled={loading}
                    style={{ marginRight: '4px' }}
                  />
                  <Button
                    icon='settings'
                    color='blue'
                    size='small'
                    title='Configure provider settings'
                    onClick={(e) => {
                      e.stopPropagation();
                      globalThis.location.href = `/brokerages/${brokerageId}/providers/${provider.id}/edit`;
                    }}
                    disabled={loading}
                  />
                  <Button
                    icon='file alternate'
                    color='teal'
                    size='small'
                    title='Manage policy types'
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProviderForModal(provider);
                      setModalOpen(true);
                    }}
                    disabled={loading}
                    style={{ marginLeft: '4px' }}
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {providers.length === 0 && !loading && (
        <Message info>
          <Message.Header>No Providers Available</Message.Header>
          <p>Please create providers in the system before configuring brokerage providers.</p>
        </Message>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#666' }}>
            Showing {providers.length} of {totalElements} providers
          </div>
          <Pagination
            activePage={currentPage + 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            siblingRange={1}
            boundaryRange={1}
            ellipsisItem={null}
            firstItem={null}
            lastItem={null}
          />
        </div>
      )}

      {selectedProviderForModal && (
        <PolicyTypeBrokerageModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedProviderForModal(null);
            loadData();
          }}
          providerId={selectedProviderForModal.id!}
          providerName={selectedProviderForModal.name}
        />
      )}
    </div>
  );
}

export default ProviderConfigSection;
